/**
 * One renderer per filter type (contract §2), all on the shared primitives:
 * Input for numbers and text, Combobox for selects, MultiSelect for
 * multi-selects, the country and language pickers for lists, a checkbox for
 * on/off. A min/max pair renders as one range field.
 */
import type { JSX, ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import { Combobox } from '@/components/ui/combobox';
import { MultiSelect } from '@/components/ui/multi-select';
import { cn } from '@/lib/utils';
import { CountryPicker } from '../components/AudiencePanel';
import { LanguagePicker } from '../components/LanguagePicker';
import { useGeoCatalog } from '../hooks/geo';
import type { SearchFilterDef, SearchParamValue } from '../api/types';
import { CheckboxField, Field } from './controls';
import { isFractionPercent } from './params';

export interface FilterControlProps {
  def: SearchFilterDef;
  value: SearchParamValue | undefined;
  onChange: (v: SearchParamValue) => void;
  error?: string | null;
  /** Why it's unavailable, e.g. the server lacks the feature. */
  disabledReason?: string | null;
  /** Rendered under the control (e.g. term suggestions under audience countries). */
  addon?: ReactNode;
}

const inputId = (key: string) => `search-filter-${key}`;

function numberFromInput(raw: string): number | null {
  if (raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** Number box in the filter's own units; percent filters are typed as 0–100. */
function NumberInput({
  def,
  value,
  onChange,
  invalid,
  placeholder,
  ariaLabel,
  disabled,
}: {
  def: SearchFilterDef;
  value: SearchParamValue | undefined;
  onChange: (v: number | null) => void;
  invalid?: boolean;
  placeholder?: string;
  ariaLabel?: string;
  disabled?: boolean;
}): JSX.Element {
  const isPct = def.type === 'percent';
  const fraction = isPct && isFractionPercent(def, typeof value === 'number' ? value : undefined);
  const shown = typeof value === 'number' ? (fraction ? Math.round(value * 1000) / 10 : value) : '';
  const suffix = isPct ? '%' : def.unit === '%' ? '%' : null;
  return (
    <div className="relative">
      <Input
        id={inputId(def.key)}
        aria-label={ariaLabel}
        aria-invalid={invalid || undefined}
        type="number"
        inputMode="decimal"
        disabled={disabled}
        min={def.min != null ? (fraction ? def.min * 100 : def.min) : undefined}
        max={def.max != null ? (fraction ? def.max * 100 : def.max) : undefined}
        step={def.step ?? (def.type === 'integer' || isPct ? 1 : 'any')}
        placeholder={placeholder ?? def.placeholder ?? 'Any'}
        value={shown}
        onChange={(ev) => {
          const n = numberFromInput(ev.target.value);
          onChange(n === null ? null : fraction ? n / 100 : n);
        }}
        className={cn(suffix && 'pr-7', invalid && 'border-destructive/70')}
      />
      {suffix && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
          {suffix}
        </span>
      )}
    </div>
  );
}

export function FilterControl({ def, value, onChange, error, disabledReason, addon }: FilterControlProps): JSX.Element {
  const disabled = !!disabledReason;
  const help = disabledReason ?? def.help;
  const invalid = !!error;

  if (def.type === 'boolean') {
    return (
      <div className="space-y-2">
        <CheckboxField
          id={inputId(def.key)}
          checked={value === true}
          onChange={onChange}
          label={def.label}
          help={help}
          cost={def.costHint}
          error={error}
          disabled={disabled}
        />
        {addon}
      </div>
    );
  }

  let control: ReactNode;
  switch (def.type) {
    case 'integer':
    case 'number':
    case 'percent':
      control = <NumberInput def={def} value={value} onChange={onChange} invalid={invalid} disabled={disabled} />;
      break;
    case 'select': {
      const options = (def.options ?? []).map((o) => ({ value: String(o.value), label: o.label }));
      control = (
        <Combobox
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(v) => {
            const opt = def.options?.find((o) => String(o.value) === v);
            onChange(opt ? opt.value : v);
          }}
          options={options}
          disabled={disabled}
          placeholder="Any"
        />
      );
      break;
    }
    case 'multiselect':
      control = (
        <MultiSelect
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
          options={(def.options ?? []).map((o) => ({ value: String(o.value), label: o.label }))}
          placeholder="Any"
          disabled={disabled}
          showClear
        />
      );
      break;
    case 'countryList':
      control = <CountryListControl def={def} value={value} onChange={onChange} invalid={invalid} />;
      break;
    case 'languageList':
      control = <LanguagePicker value={Array.isArray(value) ? value : []} onChange={onChange} />;
      break;
    default:
      control = (
        <Input
          id={inputId(def.key)}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          placeholder={def.placeholder ?? undefined}
          value={typeof value === 'string' ? value : Array.isArray(value) ? value.join(', ') : ''}
          onChange={(ev) => onChange(ev.target.value)}
          className={cn(invalid && 'border-destructive/70')}
        />
      );
  }

  return (
    <Field label={def.label} htmlFor={inputId(def.key)} help={help} cost={def.costHint} error={error}>
      {control}
      {addon}
    </Field>
  );
}

function CountryListControl({
  def,
  value,
  onChange,
  invalid,
}: {
  def: SearchFilterDef;
  value: SearchParamValue | undefined;
  onChange: (v: string[]) => void;
  invalid: boolean;
}): JSX.Element {
  const catalog = useGeoCatalog(true);
  return (
    <CountryPicker
      id={inputId(def.key)}
      countries={catalog.data?.countries ?? []}
      groups={catalog.data?.groups ?? []}
      loading={catalog.isLoading}
      value={Array.isArray(value) ? value : []}
      onChange={onChange}
      invalid={invalid}
    />
  );
}

/** "Followers  [min] – [max]" for a minX / maxX pair. */
export function RangeControl({
  label,
  min,
  max,
  values,
  onChange,
  errors,
}: {
  label: string;
  min: SearchFilterDef;
  max: SearchFilterDef;
  values: Record<string, SearchParamValue>;
  onChange: (key: string, v: SearchParamValue) => void;
  errors: Record<string, string>;
}): JSX.Element {
  const error = errors[min.key] ?? errors[max.key] ?? null;
  return (
    <Field label={label} htmlFor={inputId(min.key)} help={min.help ?? max.help} cost={min.costHint ?? max.costHint} error={error}>
      <div className="flex items-center gap-1.5">
        <NumberInput
          def={min}
          value={values[min.key]}
          onChange={(v) => onChange(min.key, v)}
          invalid={!!errors[min.key]}
          placeholder="Min"
          ariaLabel={min.label}
        />
        <span className="text-sm text-muted-foreground" aria-hidden>
          –
        </span>
        <NumberInput
          def={max}
          value={values[max.key]}
          onChange={(v) => onChange(max.key, v)}
          invalid={!!errors[max.key]}
          placeholder="Max"
          ariaLabel={max.label}
        />
      </div>
    </Field>
  );
}

/** "Min followers" + "Max followers" → "Followers". */
export function rangeLabel(min: SearchFilterDef): string {
  const rest = min.label.replace(/^min(imum)?\s+/i, '');
  return rest.charAt(0).toUpperCase() + rest.slice(1);
}
